import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Switch } from '@headlessui/react'
import { CheckCircleIcon } from '@heroicons/react/solid'
import PropTypes from 'prop-types'
import classNames from '@/utils/classNamesLocal'
import { TippyTooltip } from '@/shared/ToolTip'

import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import Card from '@/shared/Card'
import { EmailPreferencesForm, GeneralPreferencesForm } from '@/pages/Settings/EmailPreferences'

// One row per event, one column per channel. Rows and defaults come from
// Preference::NOTIFICATION_EVENTS on the server; nothing is hard-coded here.
// Every flip saves on its own. There is no Save button.
const CHANNELS = [
  { key: 'inapp', label: 'In-app' },
  { key: 'email', label: 'Email' },
  { key: 'push', label: 'Push' }
]

const Toggle = ({ label, checked, onChange, disabled }) => (
  <Switch
    checked={!!checked}
    onChange={() => { if (!disabled) { onChange() } }}
    className={classNames(
      checked ? 'bg-cccpurple' : 'bg-gray-300 dark:bg-gray-500',
      disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
      'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cccpurple'
    )}
  >
    <span className="sr-only" hidden>{label}</span>
    <span aria-hidden="true" className={classNames(
      checked ? 'translate-x-5' : 'translate-x-0',
      'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
    )} />
  </Switch>
)

Toggle.propTypes = {
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

// Current value for one channel: the stored departure, else the registry default.
const valueFor = (prefs, event, channel) => {
  const stored = prefs.notification_prefs?.[event.key]?.[channel]
  return stored === undefined ? event[channel] : stored
}

// One fetch feeds the three preference cards, so flipping "unsubscribe from
// all" below dims the email column above without a reload.
const PreferencesSection = () => {
  const { getRequest } = useQuery()
  const [data, setData] = useState(null)

  useEffect(() => {
    getRequest('/api/v3/preferences', {}, (err, jsonData) => {
      if (err) { return }
      setData(jsonData)
    })
  }, [])

  if (!data) { return null }

  const setPreferences = (preferences) => setData((d) => ({ ...d, preferences }))

  return <>
    <NotificationMatrix data={data} setData={setData} />
    <div className="col-span-4 mt-3" />
    <EmailPreferencesForm preferences={data.preferences} onUpdate={setPreferences} />
    <div className="col-span-4 mt-3" />
    <GeneralPreferencesForm preferences={data.preferences} />
  </>
}

const NotificationMatrix = ({ data, setData }) => {
  const { putpostRequest } = useQuery()
  const [currentUser] = useGlobalState('currentUser')
  const [, setToast] = useGlobalState('toast')
  const [upgradeNudge, setUpgradeNudge] = useState(false)

  const { preferences, events, groups, auditionFrequencies, hasPushDevice } = data
  const emailPaused = preferences.unsubscribe_all || !!preferences.unsub_groups?.activity_mailer
  const channels = CHANNELS.filter((c) => c.key !== 'push' || hasPushDevice)

  const save = (event, channel, value) => {
    const before = preferences
    const entry = { ...(preferences.notification_prefs?.[event.key] || {}), [channel]: value }
    setData((d) => ({ ...d, preferences: { ...preferences, notification_prefs: { ...preferences.notification_prefs, [event.key]: entry } } }))
    const body = { preference: { notifications: { [event.key]: { [channel]: value } } } }
    putpostRequest(`/api/v3/preferences/${preferences.id}`, 'PATCH', body, (err, jsonData) => {
      if (err) {
        setData((d) => ({ ...d, preferences: before }))
        setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
          <p className="text-sm font-medium text-red-600 dark:text-red-300">That didn't save</p>
          <p className="mt-1 text-sm text-red-500 dark:text-red-300">Try again in a bit? I'll wait right here.</p>
        </div>)
        return
      }
      if (jsonData?.preferences) { setData((d) => ({ ...d, preferences: jsonData.preferences })) }
    })
  }

  const cell = (event, channel) => {
    const setting = event[channel.key]
    if (setting === undefined || setting === null) { return <span className='block w-11' /> }
    if (setting === 'always') {
      return <TippyTooltip content={<>Always on</>}>
        <span className='inline-flex items-center justify-center w-11 cursor-default'>
          <CheckCircleIcon className='h-5 w-5 text-gray-300 dark:text-gray-600' aria-label='Always on' />
        </span>
      </TippyTooltip>
    }
    const locked = event.premium && !currentUser.canTogglePaidProjectEmails
    const checked = locked ? false : valueFor(preferences, event, channel.key)
    const disabled = channel.key === 'email' && emailPaused
    return <Toggle
      label={`${channel.label}: ${event.label}`}
      checked={checked}
      disabled={disabled}
      onChange={() => {
        if (locked) { setUpgradeNudge(true); return }
        save(event, channel.key, !checked)
      }}
    />
  }

  return <>
    <div className="md:col-span-1 mb-3">
      <div className="px-0 sm:px-4">
        <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">Notifications</h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Choose your destiny.
        </p>
      </div>
    </div>
    <Card title='What would you like to know?'>
      <div className='space-y-4'>
        {emailPaused && (
          <p className='p-2 rounded text-xs bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200'>
            Emails are paused by your delivery settings below, so the email switches are decoration until you flip that back on.
          </p>
        )}
        {!hasPushDevice && (
          <p className='text-xs text-gray-500 dark:text-gray-400'>
            Push shows up here as a third column once you sign in on the app.
          </p>
        )}

        {Object.entries(groups).map(([groupKey, groupLabel]) => {
          const rows = events.filter((e) => e.group === groupKey && !e.hidden)
          if (rows.length === 0) { return null }
          return (
            <div key={groupKey}>
              <div className='flex items-end gap-2 pb-1 border-b border-gray-200 dark:border-gray-600'>
                <span className='flex-1 text-sm font-semibold uppercase tracking-wide text-yellow-700 dark:text-yellow-300'>{groupLabel}</span>
                {channels.map((c) => (
                  <span key={c.key} className='w-11 text-center text-xs text-gray-500 dark:text-gray-400'>{c.label}</span>
                ))}
              </div>
              {rows.map((event) => {
                const showFrequency = event.frequency && valueFor(preferences, event, 'email') && !emailPaused
                return (
                  <div key={event.key} className='py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0'>
                    <div className='flex items-center gap-2'>
                      <div className='flex-1 min-w-0'>
                        <p className='text-sm text-gray-700 dark:text-gray-300'>
                          {event.label}
                          {event.premium && (
                            <Link to='/membership' className='ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold uppercase tracking-wide bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'>
                              Premium perk
                            </Link>
                          )}
                        </p>
                      </div>
                      {channels.map((c) => <React.Fragment key={c.key}>{cell(event, c)}</React.Fragment>)}
                    </div>
                    {event.premium && upgradeNudge && !currentUser.canTogglePaidProjectEmails && (
                      <p className='text-xs mt-1'>
                        <Link to='/membership' className='text-cccblue hover:underline'>Upgrade and I'll start sending these</Link>
                      </p>
                    )}
                    {showFrequency && (
                      <div className='flex justify-between items-center mt-2 ml-4'>
                        <span className='text-sm text-gray-700 dark:text-gray-300'>Bundle those emails</span>
                        <select
                          value={valueFor(preferences, event, 'frequency')}
                          onChange={(e) => save(event, 'frequency', e.target.value)}
                          className='block py-1.5 px-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-cccpurple focus:border-cccpurple sm:text-sm'
                        >
                          {Object.entries(auditionFrequencies).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )
        })}

        <div className='p-2 rounded bg-gray-50 dark:bg-gray-700'>
          <p className='text-sm text-gray-600 dark:text-gray-300'>
            <span className='font-medium'>New project emails</span> live with your <Link to='/saved_searches' className='text-cccblue hover:underline'>saved searches</Link>. That's where you pick daily or weekly.
          </p>
        </div>
      </div>
    </Card>
  </>
}

NotificationMatrix.propTypes = {
  data: PropTypes.object.isRequired,
  setData: PropTypes.func.isRequired
}

export default PreferencesSection
